import { useContext, useState } from "react";
import css from "../Approvals/css/view-modal.module.css";
import styled from "@emotion/styled";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import Select from "../../../customs/SelectInput";
import Button from "../../../customs/Button";
import apiCall from "../../../utils/apiCall";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { NotificationContext } from "../../../providers/notification";
import DocumentBar from "./DocumentBar";
import { ContainerWrapper, NewContainer } from "./styles";
import {
  CorporateUploadProps,
  ClassificationResponse,
  Classification,
} from "./type";

interface Payload {
  ApplicantId: string;
  ClassificationTypeId: string;
  values?: FormikValues;
}

const corporateUploadData = [
  {
    title: "Certification of Incorporation",
    uploadType: "CAC",
  },
  {
    title: "Current Annual Report & Financial Statement (if applicable)",
    uploadType: "Financial",
  },
  {
    title: "Company’s Profile",
    uploadType: "Profile",
  },
  {
    title:
      "Reference from a professional member of the Institute (Full member or fellow) ",
    uploadType: "Referrer",
  },
];

const CorporateUpload: React.FC<CorporateUploadProps> = ({
  data = [],
  applicantId,
}) => {
  const { showNotification } = useContext(NotificationContext);
  const [classificationType, setClassificationType] = useState("");

  const getAllClassification = async (): Promise<ClassificationResponse> => {
    return (await apiCall().get("/SetUp/GetAllClassificationSector")).data;
  };

  const {
    data: classificationData,
    isLoading,
    isError,
    error,
  } = useQuery(["get-all-classification"], getAllClassification, {
    retry: 0,
    keepPreviousData: true,
    enabled: true,
  });

  const getAllClassificationError = error as AxiosError;
  const getAllClassificationErrorMessage = getAllClassificationError?.message;

  const updateApplicantClassificationType = async (data: Payload) => {
    const url = `/Applicant/UpdateApplicantClassificationType?ApplicantId=${applicantId}&ClassificationTypeId=${classificationType}`;
    return await apiCall().post(url, data);
  };

  const updateApplicantClassificationTypeMutation = useMutation(
    ["update-applicant-classification"],
    updateApplicantClassificationType,
    { retry: 0 }
  );

  const updateApplicantClassificationTypeHandler = async (
    values: FormikValues
  ) => {
    setClassificationType(values?.classification);
    const payload: Payload = {
      ApplicantId: applicantId,
      ClassificationTypeId: values?.classification,
    };

    try {
      await updateApplicantClassificationTypeMutation.mutateAsync(payload, {
        onSuccess: () => {
          showNotification({
            type: "success",
            message: "Applicant classification type successfully updated",
          });
        },
      });
    } catch (error: any) {
      showNotification({
        type: "error",
        message: error?.response?.data?.Message || error?.message,
      });
    }
  };

  const classificationOptions =
    classificationData?.Data && classificationData?.Data.length > 0 ? (
      classificationData?.Data.map((data: Classification) => (
        <option key={data?.Id} value={data?.Id}>
          {data?.ClassSector}
        </option>
      ))
    ) : (
      <option disabled>
        {isLoading
          ? "Loading..."
          : isError
          ? getAllClassificationErrorMessage
          : ""}
      </option>
    );

  const formik = useFormik<FormikValues>({
    initialValues: {
      classification: "",
    },
    onSubmit: (values) => {
      updateApplicantClassificationTypeHandler(values);
    },
  });

  return (
    <div className={css.edDiv}>
      <p className={css.title}>Corporate Uploads</p>
      {
        //filter Title of each documents
        data?.length > 0
          ? data.map((eData) => {
              let result = corporateUploadData.filter(
                (items) => items?.uploadType === eData.UploadType
              );
              return (
                <ContainerWrapper>
                  <div>
                    <div className="header">
                      {" "}
                      {result.length !== 0 ? result[0].title : ""}
                    </div>
                    <NewContainer>
                      <DocumentBar
                        docName={eData?.UploadType}
                        documentUrl={eData?.FileUrl}
                        onChange={() => {}}
                      />
                    </NewContainer>
                  </div>
                </ContainerWrapper>
              );
            })
          : "Not Available"
      }
      <FormikProvider value={formik}>
        <form onSubmit={formik?.handleSubmit}>
          <InputWrapper>
            <Field
              name="classification"
              type="text"
              as={Select}
              label="Classification"
              options={classificationOptions}
            />
          </InputWrapper>
          <ButtonWrapper>
            <StyledButton
              type="submit"
              text={
                updateApplicantClassificationTypeMutation.isLoading
                  ? "Saving..."
                  : "Save Changes"
              }
            />
          </ButtonWrapper>
        </form>
      </FormikProvider>
    </div>
  );
};

export default CorporateUpload;

const InputWrapper = styled.div`
  display: flex;
  gap: 106px;
`;

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  margin-block: 50px;
`;

const StyledButton = styled(Button)`
  padding-block: 1rem;
  padding-inline: 2rem;
`;
